import { type ReactNode, useLayoutEffect, useRef } from 'react'
import './section-nav.css'

export type SectionTab = { key: string; label: ReactNode }

/**
 * A horizontal section switch — the thin tab strip a surface uses to move
 * between its areas (platform: Tasks / App config). Shared so every surface's
 * section menu reads and behaves the same.
 *
 * **A strip that moves between views of ONE thing navigates with `replace`.**
 * Which view of a record or a list you are reading is the same class of fact as
 * its search term, which `useListState` already replaces for: the push is
 * opening a different thing. A tab that pushes turns the back control into a
 * walk through the strip, and the list it was meant to return to sits as many
 * presses away as the reader pressed tabs. The strip cannot enforce this — it
 * raises `onChange` and the caller navigates — so it is stated here, where
 * somebody wiring a new one reads it.
 */
export function SectionNav<Key extends string>({
  tabs,
  active,
  onChange,
  label = 'Sections',
  variant = 'page',
}: {
  /** A tab's name — text, or text in the script a public page chose. */
  tabs: readonly { key: Key; label: ReactNode }[]
  active: Key
  onChange: (key: Key) => void
  label?: string
  /** `sheet` runs the strip to the panel's own walls and drops the gap beneath
   *  it — inside a Sheet the body supplies its own inset, and a second one
   *  makes the tabs look inset from a frame they are part of. `public` is the
   *  strip on a published page, set at the body size, with the gap below it
   *  left to the page it sits in. */
  variant?: 'page' | 'sheet' | 'public'
}) {
  /* The strip scrolls rather than wrapping, so the tab that is showing is
     brought inside it: a strip opened on its fifth tab at a phone's width would
     otherwise name everything but the page being read. Only the strip moves —
     never the page around it. */
  const strip = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const nav = strip.current
    const tab = nav?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!nav || !tab || active === undefined) return
    const start = tab.offsetLeft - nav.offsetLeft
    const end = start + tab.offsetWidth
    if (start < nav.scrollLeft) nav.scrollLeft = start
    else if (end > nav.scrollLeft + nav.clientWidth)
      nav.scrollLeft = end - nav.clientWidth
  }, [active])

  return (
    <nav
      ref={strip}
      className={`section-nav section-nav--${variant}`}
      aria-label={label}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className="section-nav__tab"
          aria-current={active === t.key ? 'page' : undefined}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  )
}
